/**
 * Manpower P&L — Workers listing, as thin config over the generic <ResourceListing>.
 * List + vendor filter + add-to-vendor (mapping) + edit + per-worker salary card.
 */
import React, { useState } from 'react';
import { Pencil, IndianRupee } from 'lucide-react';
import {
	ResourceListing,
	Column,
	RowAction,
	StatusPill,
} from '../../../components/manpower/ResourceListing';
import { useWorkerData } from '../hooks/useWorkerData';
import { useVendorData } from '../../manpower-vendors/hooks/useVendorData';
import { WorkerModal } from './WorkerModal';
import { SalaryModal } from './SalaryModal';
import type { Worker } from '../../../services/manpowerWorkersApi';

interface WorkerListingProps {
	/** Locked to one vendor = the drill-down sub-page: hides the vendor filter,
	 *  titles the screen with the vendor name, and pre-locks Add Worker to it. */
	lockedVendorId?: number;
	backTo?: { label: string; href: string };
}

export const WorkerListing: React.FC<WorkerListingProps> = ({ lockedVendorId, backTo }) => {
	const [vendorId, setVendorId] = useState<number | ''>('');
	const [workerModal, setWorkerModal] = useState(false);
	const [editing, setEditing] = useState<Worker | null>(null);
	const [salaryFor, setSalaryFor] = useState<Worker | null>(null);
	const [toast, setToast] = useState<string | null>(null);

	const locked = lockedVendorId != null;
	const effectiveVendorId = locked ? lockedVendorId : vendorId === '' ? undefined : vendorId;
	const { data, isLoading, error } = useWorkerData(
		effectiveVendorId != null ? { vendorId: effectiveVendorId } : undefined
	);
	const { data: vendorsResp } = useVendorData();
	const vendors = vendorsResp?.data ?? [];
	const workers: Worker[] = data?.data ?? [];

	const showToast = (msg: string) => {
		setToast(msg);
		window.setTimeout(() => setToast(null), 3000);
	};

	// Worker rows carry vendor_id, not the name — resolve for display.
	const vendorName = (id?: number) => vendors.find(v => v.id === id)?.name ?? (id != null ? `#${id}` : '—');

	const columns: Column<Worker>[] = [
		{ header: 'ID', render: w => w.id ?? '—' },
		{ header: 'Name', render: w => w.name ?? '—', mobile: 'title' },
		// on the vendor sub-page every row is the same vendor — hide the subtitle there
		{ header: 'Vendor', render: w => vendorName(w.vendor_id), mobile: locked ? 'off' : 'subtitle' },
		{ header: 'Category', render: w => <StatusPill text={w.worker_category} tone='blue' /> },
		{ header: 'Shift', render: w => w.shift_label ?? '—' },
		{ header: 'Status', render: w => <StatusPill text={w.status} tone={w.status === 'active' ? 'green' : 'gray'} /> },
	];

	const actions: RowAction<Worker>[] = [
		{
			label: 'Edit',
			colorClass: 'text-blue-600',
			icon: <Pencil className='h-4 w-4' />,
			onClick: w => {
				setEditing(w);
				setWorkerModal(true);
			},
		},
		{ label: 'Salary', colorClass: 'text-green-700', icon: <IndianRupee className='h-4 w-4' />, onClick: w => setSalaryFor(w) },
	];

	return (
		<ResourceListing<Worker>
			title={locked ? vendorName(lockedVendorId) : 'Workers'}
			subtitle={locked ? 'Workers' : 'Manpower P&L — workers & salary'}
			backTo={backTo}
			phoneTable
			addLabel='+ Add Worker'
			onAdd={() => {
				setEditing(null);
				setWorkerModal(true);
			}}
			filter={
				locked
					? undefined
					: {
							label: 'Vendor',
							value: vendorId,
							onChange: v => setVendorId(v as number | ''),
							allLabel: 'All vendors',
							options: vendors.map(v => ({ id: v.id as number, label: v.name as string })),
						}
			}
			columns={columns}
			rows={workers}
			rowKey={(w, i) => w.id ?? i}
			actions={actions}
			isLoading={isLoading}
			error={error}
			toast={toast}
		>
			<WorkerModal
				open={workerModal}
				editing={editing}
				lockedVendorId={lockedVendorId}
				onClose={() => setWorkerModal(false)}
				onSuccess={showToast}
			/>
			<SalaryModal
				open={!!salaryFor}
				worker={salaryFor}
				onClose={() => setSalaryFor(null)}
				onSuccess={showToast}
			/>
		</ResourceListing>
	);
};

export default WorkerListing;
